import { useEffect, useId, useRef, useState, type FormEvent } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  fetchContactCaptcha,
  sendGetInTouchPublic,
  type GetInTouchResponse,
} from "../app/api/email"
import "./GetInTouchPanel.scss"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

type TurnstileApi = {
  render: (
    el: HTMLElement,
    params: {
      sitekey: string
      action?: string
      theme?: string
      size?: string
      callback?: (token: string) => void
      "expired-callback"?: () => void
      "error-callback"?: () => void
    }
  ) => string
  reset: (widgetId: string) => void
  remove?: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let turnstileLoader: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Turnstile is not available."))
  }
  if (window.turnstile) {
    return Promise.resolve()
  }
  if (turnstileLoader) {
    return turnstileLoader
  }
  turnstileLoader = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SRC}"]`
    )
    if (existing) {
      if (window.turnstile) {
        resolve()
        return
      }
      existing.addEventListener("load", () => resolve(), { once: true })
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load captcha.")),
        { once: true }
      )
      return
    }
    const script = document.createElement("script")
    script.src = TURNSTILE_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("Failed to load captcha."))
    document.head.appendChild(script)
  })
  return turnstileLoader
}

type GetInTouchPanelProps = {
  source?: string
  onSubmit?: (payload: {
    email: string
    turnstileToken: string
    website: string
    source?: string
  }) => Promise<GetInTouchResponse>
  onClose?: () => void
}

export default function GetInTouchPanel({
  source = "labwick-site",
  onSubmit = sendGetInTouchPublic,
  onClose,
}: GetInTouchPanelProps) {
  const fieldId = useId()
  const turnstileHostRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [email, setEmail] = useState("")
  const [website, setWebsite] = useState("")
  const [turnstileToken, setTurnstileToken] = useState("")
  const [captchaReady, setCaptchaReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    let cancelled = false

    const mountCaptcha = async () => {
      setError(null)
      setCaptchaReady(false)
      setTurnstileToken("")
      const captcha = await fetchContactCaptcha()
      if (cancelled) return
      if (!captcha.ok) {
        setError(captcha.message)
        return
      }
      try {
        await loadTurnstileScript()
        if (cancelled || !window.turnstile || !turnstileHostRef.current) return
        widgetIdRef.current = window.turnstile.render(turnstileHostRef.current, {
          sitekey: captcha.siteKey,
          action: "contact",
          theme: "light",
          size: "normal",
          callback: (token) => {
            setTurnstileToken(String(token || ""))
          },
          "expired-callback": () => {
            setTurnstileToken("")
          },
          "error-callback": () => {
            setTurnstileToken("")
          },
        })
        setCaptchaReady(true)
      } catch {
        if (!cancelled) {
          setError("Captcha unavailable — refresh the page.")
        }
      }
    }

    void mountCaptcha()

    return () => {
      cancelled = true
      const widgetId = widgetIdRef.current
      widgetIdRef.current = null
      if (widgetId && window.turnstile?.remove) {
        try {
          window.turnstile.remove(widgetId)
        } catch {
          // Widget already gone with the host node.
        }
      }
    }
  }, [])

  const resetTurnstile = () => {
    setTurnstileToken("")
    const widgetId = widgetIdRef.current
    if (widgetId && window.turnstile) {
      try {
        window.turnstile.reset(widgetId)
      } catch {
        // Ignore; a new token is required before the next submit.
      }
    }
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const trimmedEmail = email.trim()
    if (!EMAIL_RE.test(trimmedEmail)) {
      setError("Please enter a valid email address.")
      return
    }
    if (!turnstileToken) {
      setError("Please complete the captcha.")
      return
    }
    setError(null)
    setLoading(true)
    try {
      const result = await onSubmit({
        email: trimmedEmail,
        turnstileToken,
        website: website.trim(),
        source,
      })
      if (result.success) {
        setSuccess(true)
        setEmail("")
        setWebsite("")
        resetTurnstile()
      } else {
        setError(result.message || "Failed to send message.")
        resetTurnstile()
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to send message.")
      resetTurnstile()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="lw-git">
      {success ? (
        <div className="lw-git-body">
          <p className="lw-git-status is-ok" role="status">
            Thanks — we will be in touch.
          </p>
          {onClose ? (
            <Button type="button" className="mt-4" onClick={onClose}>
              Close
            </Button>
          ) : null}
        </div>
      ) : (
        <form className="lw-git-form" onSubmit={handleSubmit} noValidate>
          <label className="lw-git-visually-hidden" htmlFor={`${fieldId}-email`}>
            Email
          </label>
          <Input
            id={`${fieldId}-email`}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            required
            autoComplete="email"
          />
          <div className="lw-git-hp" aria-hidden="true">
            <label htmlFor={`${fieldId}-website`}>Website</label>
            <input
              id={`${fieldId}-website`}
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
            />
          </div>
          <div
            ref={turnstileHostRef}
            className="lw-git-turnstile"
            aria-label="Captcha"
          />
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <Button type="submit" disabled={loading || !captchaReady}>
            {loading ? "Sending..." : "Send"}
          </Button>
        </form>
      )}
    </div>
  )
}
